/* eslint-disable no-inline-comments */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  Container,
  Typography,
  ImageList,
  ImageListItem,
  useMediaQuery
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HelpOutline from '@material-ui/icons/HelpOutline';
import { IconButton } from '@material-ui/core';

import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';

import ImageButton from '../../../Components/Buttons/ImageButton';
import InvoiceHelpDialog from '../../../Components/Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';
import { HELP_TEXT } from '../../../Utils/enums';

import DuplicateDialog from '../../Duplicates/Duplicates';

import ContractButton from '../../../Components/CustomIcons/IcContractButton';
import CouponButton from '../../../Components/CustomIcons/IcCouponButton';
import DuplicatesButton from '../../../Components/CustomIcons/IcDuplicatesButton';

import { useFetchCommunications } from '../../../API/Communications/useFetchCommunications';
import SlideShow from '../../../Components/SlideShow/SlideShow';
import BrillaIcon from '../../../Components/CustomIcons/BrillaIcon';

import { OpenNewTab } from '../../../Utils/Misc/Links';
import { BrillaHomeLink, BrillaQuotaLink } from '../../../Configs/Links';
import DigitalInvoiceBannerDialog from '../../../Components/Dialogs/DigitalInvoiceBannerDialog';
import { Company } from '../../../Configs/general';
import financiacionBrillaOpc2 from '../../../Assets/images/efg/popup-brilla-vertical-600x836-final.png';

const NoAuthHomeEFG = () => {
  // * STATE HOOKS
  const [helpDialogType, setHelpDialogType] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [openCommunications, setOpenCommunications] = useState(false);

  // * OTHER HOOKS
  const classes = useStyles();
  const [communications, getCommunications] = useFetchCommunications();

  const isMobileSize = useMediaQuery(theme => theme.breakpoints.down('xs'));
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.only('sm'));
  const deviceColumns = useMemo(() => {
    if (isMobileSize) {
      return 2;
    }

    if (isSmallScreen) {
      return 3;
    }

    return 4;
  }, [isMobileSize, isSmallScreen]);

  // Fetch No Auth banner communications if any
  useEffect(() => {
    getCommunications();
  }, [getCommunications]);

  // * FUNCTIONS

  const onDuplicateDialogClick = useCallback(() => {
    setDuplicateDialogOpen(true);
  }, []);

  const _handleRoute = method => {
    history.push(ROUTE_NAMES.payments, {
      type: method,
      value: ''
    });
  };

  const _goToSignup = () => {
    history.push(ROUTE_NAMES.login);
  };

  const linkToBrillaQuota = () => {
    OpenNewTab(BrillaQuotaLink);
  };

  const _goToVerifyVisit = () => {
    history.push(ROUTE_NAMES.verifyVisit);
  };
  const renderButtonText = (text, type) => {
    return (
      <>
        {text}
        <IconButton
          onClick={() => setHelpDialogType(type)}
          className={classes.helpIcon}
          aria-label="help"
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </>
    );
  };

  return (
    <Container
      id="NoAuthHome_div_container"
      maxWidth="md"
      className={classes.homeContainer}
    >
      <DigitalInvoiceBannerDialog
        open={openCommunications}
        setOpenDialog={setOpenCommunications}
        image={financiacionBrillaOpc2}
        imageTitle="Cupón Brilla"
        imageLinkUrl={BrillaHomeLink}
      />
      {duplicateDialogOpen && (
        <DuplicateDialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
        />
      )}
      <InvoiceHelpDialog
        open={Boolean(helpDialogType)}
        type={helpDialogType}
        onClose={() => setHelpDialogType(null)}
        title={HELP_TEXT[helpDialogType]}
      />
      <SlideShow slides={communications} />
      <Typography variant="h2" className={clsx(classes.text, classes.title)}>
        Portal de servicio al cliente de{' '}
        <span className={classes.titleCompany}>{Company.name}.</span>
      </Typography>
      <Container maxWidth="sm" className={classes.textAreaContainer}>
        <Typography
          variant="h2"
          className={clsx(classes.text, classes.welcome)}
        >
          ¡Bienvenido!
        </Typography>
        <Typography
          variant="h6"
          className={clsx(classes.text, classes.subText)}
        >
          Para realizar los trámites de tu servicio de gas, de forma fácil y
          segura.{' '}
          <span className={classes.loginText} onClick={_goToSignup}>
            Iniciar sesión
          </span>
        </Typography>
      </Container>
      <ImageList
        rowHeight="auto"
        cols={deviceColumns}
        classes={{ root: classes.optionsContainer }}
      >
        <ImageListItem classes={{ item: classes.optionTile }}>
          <ImageButton
            id="NoAuthHome_button_coupon"
            icon={CouponButton}
            size="l"
            title={renderButtonText('Paga con tu número de cupón', 'coupon')}
            aria-label="paga_con_tu_cupón"
            onClick={() => _handleRoute('coupon')}
          />
        </ImageListItem>
        <ImageListItem classes={{ item: classes.optionTile }}>
          <ImageButton
            id="NoAuthHome_button_contract"
            icon={ContractButton}
            size="l"
            title={renderButtonText(
              `Paga con tu número de ${Company.contractConjugation.regular.singular.main}`,
              'contract'
            )}
            aria-label="paga_con_tu_contrato"
            onClick={() => _handleRoute('contract')}
          />
        </ImageListItem>
        <ImageListItem classes={{ item: classes.optionTile }}>
          <ImageButton
            id="NoAuthHome_button_duplicates"
            icon={DuplicatesButton}
            size="l"
            title="Generar cupón de pago"
            aria-label="genera_un_cupon_de_pago"
            onClick={onDuplicateDialogClick}
          />
        </ImageListItem>
        <ImageListItem classes={{ item: classes.optionTile }}>
          <ImageButton
            id="AuthHome_button_brilla"
            icon={BrillaIcon}
            size="l"
            title="Consulta tu cupo Brilla"
            aria-label="Consulta tu cupo Brilla"
            onClick={linkToBrillaQuota}
          />
        </ImageListItem>
      </ImageList>
      <Typography
        variant="h6"
        className={clsx(classes.text, classes.subText, classes.visit)}
      >
        Consulta información de tu visita{' '}
        <span className={classes.loginText} onClick={_goToVerifyVisit}>
          aquí
        </span>
      </Typography>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  homeContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: '5vh'
  },
  text: {
    textAlign: 'center',
    lineHeight: 1.43
  },
  title: {
    // * Responsive (with banner)
    [theme.breakpoints.up('xs')]: {
      fontSize: '1em',
      paddingTop: 0 // (30)
    },
    [theme.breakpoints.up('xm')]: {
      fontSize: '1.25em',
      paddingTop: 0 // (35)
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: 28,
      paddingTop: 10 // 40
    }
  },
  textAreaContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('xm')]: {
      marginTop: theme.spacing(3)
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4)
    }
  },
  welcome: {
    // * Responsive
    [theme.breakpoints.up('xs')]: {
      fontSize: '2em'
    },
    [theme.breakpoints.up('xm')]: {
      fontSize: '2.25em'
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '3em'
    }
  },
  titleCompany: {
    fontWeight: 'bold'
  },
  subText: {
    color: theme.palette.color.default,
    // * Responsive
    [theme.breakpoints.up('xs')]: {
      marginTop: theme.spacing(2.5),
      fontSize: '0.75em'
    },
    [theme.breakpoints.up('xm')]: {
      marginTop: theme.spacing(3),
      fontSize: '0.83em'
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4),
      fontSize: '1em',
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5)
    }
  },
  loginText: {
    color: theme.palette.primary.dark,
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  selectPayment: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    height: 200,
    // * Responsive
    [theme.breakpoints.up('xs')]: {
      marginTop: theme.spacing(3),
      fontSize: '0.75em'
    },
    [theme.breakpoints.up('xm')]: {
      marginTop: theme.spacing(3.5),
      marginLeft: theme.spacing(1)
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4)
    }
  },
  helpIcon: {
    padding: 0,
    marginLeft: 5
  },
  optionsContainer: {
    display: 'flex',
    minHeight: 250,
    marginTop: '38px !important',
    marginBottom: '38px !important',
    overflowY: 'unset !important',
    [theme.breakpoints.up('xs')]: {
      marginTop: '20px !important'
    },
    [theme.breakpoints.up('xm')]: {
      marginTop: '28px !important'
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: '38px !important'
    }
  },
  optionTile: {
    display: 'flex',
    width: '100%',
    height: '100%',
    paddingTop: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(4.5)
  },
  visit: {
    // * Responsive
    [theme.breakpoints.up('xs')]: {
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(10),
      fontSize: '0.75em'
    },
    [theme.breakpoints.up('xm')]: {
      marginTop: theme.spacing(3),
      fontSize: '0.83em'
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4),
      fontSize: '1em',
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5)
    }
  }
}));

export default NoAuthHomeEFG;
